import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import i18next from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import * as API from '../api';
import * as DateUtils from '../utils/date';
import { useAppBar } from '../context/AppBar';
import { useAppContainer } from '../context/AppContainer';
import CourseResourcesList from '../components/CourseResourcesList';
import GoogleMap from '../components/GoogleMap';
import OpenClassSessionAttendeesInfoCell from '../components/OpenClassSessionAttendeesInfoCell';
import OpenClassSessionStatusBadge from '../components/OpenClassSessionStatusBadge';
import SafeLifeSpin from '../components/SafeLifeSpin';
import { removeCountryFromAddress } from '../utils/address';

import { InfoCell } from './CourseInstanceDetails';

type OpenClassSessionDetailsParams = {
  sessionId: string;
};

const OpenClassSessionDetails: React.FC = () => {
  const { sessionId: sessionIdParam } =
    useParams<OpenClassSessionDetailsParams>();
  const sessionId = Number(sessionIdParam);
  const { t } = useTranslation();
  const [, setAppBarInterface] = useAppBar();
  const [, setCurrentClasses] = useAppContainer();
  const sessionQuery = useQuery({
    queryKey: ['openClassSession', sessionId],
    queryFn: () => API.getOpenClassSession(sessionId),
    enabled: Number.isInteger(sessionId),
    retry: false,
  });
  const session = sessionQuery.data?.data;
  const courseResourcesQuery = useQuery({
    queryKey: ['courseResources', session?.courseId],
    queryFn: () => API.getCourseResources(session!.courseId), // eslint-disable-line @typescript-eslint/no-non-null-assertion
    enabled: session !== undefined,
    retry: false,
  });

  useEffect(() => {
    setAppBarInterface({
      title: t('views.OpenClassSessionDetails.title'),
      showBackButton: true,
    });
    setCurrentClasses('bg-white');
    window.scrollTo(0, 0);
  }, [i18next.language]); // eslint-disable-line react-hooks/exhaustive-deps

  if (sessionQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-5/6">
        <SafeLifeSpin size="large" />
      </div>
    );
  }

  if (sessionQuery.isError || !session) {
    const unauthorized =
      isAxiosError(sessionQuery.error) &&
      sessionQuery.error.response?.status === 403;

    return (
      <div className="p-4 text-center">
        {t(
          unauthorized
            ? 'views.OpenClassSessionDetails.unauthorized'
            : 'views.OpenClassSessionDetails.unavailable',
        )}
      </div>
    );
  }

  const start = dayjs(session.startAt);
  const end = dayjs(session.endAt);

  return (
    <div className="py-2 px-3 bg-white h-full view-max-width max-w-sm">
      <div className="flex justify-between align-middle items-center pt-4">
        <h2 className="sm:max-w-xs font-semibold text-2xl break-words">
          {session.courseName}
        </h2>
        <OpenClassSessionStatusBadge pill status={session.status} />
      </div>
      <InfoCell title={t('views.OpenClassSessionDetails.type')}>
        {t('components.OpenClassSessionCard.openClass')}
      </InfoCell>
      <InfoCell title={t('common.time')}>
        <div className="flex flex-col">
          <span>{DateUtils.formatDateRange(start, end)}</span>
          <span>{DateUtils.formatTime(start, end)}</span>
        </div>
      </InfoCell>
      <InfoCell title={t('views.OpenClassSessionDetails.session')}>
        {t('components.OpenClassSessionCard.session', {
          number: session.sessionNumber,
          count: session.sessionCount,
        })}
      </InfoCell>
      <InfoCell title={t('views.CourseInstanceDetails.courseInformation')}>
        <CourseResourcesList
          loadingCourseResources={courseResourcesQuery.isFetching}
          courseResources={courseResourcesQuery.data?.data ?? []}
        />
      </InfoCell>
      <OpenClassSessionAttendeesInfoCell session={session} />
      <InfoCell title={t('common.address')}>
        <div className="pb-3">
          {removeCountryFromAddress(session.location ?? undefined)}
        </div>
        {session.lat != null && session.lng != null && (
          <GoogleMap
            className="w-full rounded-lg max-w-sm"
            height={230}
            pinLocation={{ lat: session.lat, lng: session.lng }}
            width={335}
          />
        )}
      </InfoCell>
      <InfoCell title={t('common.comment')}>
        <div style={{ whiteSpace: 'pre-line' }}>{session.comment ?? '-'}</div>
      </InfoCell>
      <InfoCell title={t('views.OpenClassSessionDetails.instructors')}>
        {session.instructors.map(({ name }) => name).join(', ') || '-'}
      </InfoCell>
    </div>
  );
};

export default OpenClassSessionDetails;
